/**
 * Middleware factory for the Notifications plugin.
 */
export default function createMiddleware (bottle) {
  // List of all listeners that are waiting for actions.
  const actionListeners = []

  /*
   * The middleware that will be given to the Factory.
   */
  const middleware = context => next => action => {
    // Act after the action has gone through the reducers.
    const returnVal = next(action)

    let i = actionListeners.length
    // Start from the end of the list so we can remove items from the list
    //    without making it complicated to iterate over everything.
    while (i--) {
      const listener = actionListeners[i]
      /*
       * If the action matches a listener, remove that listener from the list
       *    then resolve its promise to unpause the operation that called it.
       */
      if (listener.pattern(action)) {
        actionListeners.splice(i, 1)
        if (listener.timeoutId) {
          clearTimeout(listener.timeoutId)
        }
        listener.resolve(action)
      }
    }
    return returnVal
  }

  /**
   * Utility function for operations to pause until an action is dispatched.
   *
   * Using this function should be avoided if possible. This was created as a stop-gap
   *    so pre-redesign sagas and post-redesign operations could still work together.
   *    ie. so a saga that is triggered by an action, then responds by dispatching
   *    another action, can be used by an operation.
   *
   * @method waitForAction
   * @param {Function} pattern Function that decides whether the action should trigger the listener.
   * @param {number} [timeout] Delay, in milleseconds, to wait for the action before rejecting the promise.
   * @return {Promise} Promise that resolves when the action has been dispatched.
   * @example
   * const takeAction = container.Notifications.takeAction
   * const action = await takeAction((action) => ...)
   */
  function takeAction (pattern, timeout) {
    if (typeof pattern === 'string') {
      // If a single action type was passed in, convert it to a function for consistency.
      const actionType = pattern
      pattern = action => action.type === actionType
    }

    return new Promise((resolve, reject) => {
      let timeoutId
      // If a timeout was provided, reject the promise after that long.
      if (typeof timeout === 'number') {
        timeoutId = setTimeout(reject, timeout)
      }

      actionListeners.push({
        pattern,
        resolve,
        timeoutId
      })
    })
  }

  // Add the utility to the bottle container.
  bottle.value('Notifications.takeAction', takeAction)

  return middleware
}
